import * as AppleAuthentication from 'expo-apple-authentication';
import { View, Platform, StyleSheet, Text, Pressable, Animated } from 'react-native';
import { YStack, Button } from 'tamagui';
import { DefaultRootState } from '@reduxLocal/persistState';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import DeviceInfo from 'react-native-device-info';
import getTCConfirm from '@components/modals/TCModal/utils/getTCConfirm';
import { HIDE_LOADING_MODAL, INITIALISE_MENU, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, SHOW_OTP, SHOW_TC_MODAL, SUCCESS } from '@reduxLocal/actions/types';
import { useNavigation } from '@react-navigation/native';
import { AntDesign } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import { LinearGradient } from 'expo-linear-gradient';
import { getAuthSessionGuard } from '@utils/authSessionGuard';

// Get auth session guard singleton
const authGuard = getAuthSessionGuard();

// TypeScript declarations for Apple Sign In Web SDK
declare global {
  interface Window {
    AppleID: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<{
          authorization: {
            code: string;
            id_token: string;
            state: string;
          };
          user?: {
            email?: string;
            name?: {
              firstName?: string;
              lastName?: string;
            };
          };
        }>;
      };
    };
  }
}

// Apple Web Client ID - Replace with your actual Apple Client ID from Apple Developer Console
const APPLE_WEB_CLIENT_ID = process.env.EXPO_PUBLIC_APPLE_WEB_CLIENT_ID || 'com.smartsaas.signin';

const AppleButton = ({ code, productId }: { code?: string; productId?: string }) => {
  const navigation = useNavigation();
  const notificationToken = useSelector(
    (state: DefaultRootState) => state.app.notificationToken
  );
  const dispatch = useDispatch();
  // Animation for code indicator
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Animation effect for code indicator
  useEffect(() => {
    if (code) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }).start();
    }
  }, [code, fadeAnim]);

  // Check platform capability for Apple Sign In
  const [isAppleAvailable, setIsAppleAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAppleAvailability = async () => {
      try {
        if (Platform.OS === 'ios') {
          const available = await AppleAuthentication.isAvailableAsync();
          setIsAppleAvailable(available);
        } else if (Platform.OS === 'web') {
          // For web, we'll show a custom styled button that explains Apple Sign In
          setIsAppleAvailable(true);
        } else {
          setIsAppleAvailable(false);
        }
      } catch (error) {
        console.log('Apple Sign In availability check failed:', error);
        setIsAppleAvailable(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAppleAvailability();
  }, []);

  // Load Apple's Sign In JS SDK for web
  const loadAppleSignInScript = () => {
    return new Promise((resolve, reject) => {
      if (Platform.OS !== 'web') {
        resolve(true);
        return;
      }
      
      if (window.AppleID) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      script.onload = () => {
        window.AppleID.auth.init({
          clientId: APPLE_WEB_CLIENT_ID,
          scope: 'name email',
          redirectURI: window.location.origin + '/auth/apple/callback',
          state: 'apple-signin',
          usePopup: true
        });
        resolve(true);
      };
      script.onerror = () => reject(new Error('Failed to load Apple Sign In script'));
      document.head.appendChild(script);
    });
  };

  // Handle Apple Sign In for different platforms
  const handleAppleSignIn = async () => {
    // Check if another auth session is active
    if (authGuard.isSessionActive()) {
      const activeSession = authGuard.getActiveSession();
      console.warn(`Cannot start Apple auth - ${activeSession} is already active`);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Please wait for ${activeSession} authentication to complete`,
          type: 'error'
        }
      });
      return;
    }

    try {
      if (Platform.OS === 'ios' && isAppleAvailable) {
        // Native iOS Apple Sign In with guard
        const credential = await authGuard.startSession('Apple', async () => {
          return await AppleAuthentication.signInAsync({
            requestedScopes: [
              AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
              AppleAuthentication.AppleAuthenticationScope.EMAIL,
            ],
          });
        });

        const newOAuthUser = {
          surname: credential.fullName?.familyName,
          name: credential.fullName?.givenName,
          authId: credential.user,
          email: credential.email,
          oauthToken: credential.identityToken,
          type: 'apple',
          device: {
            brand: DeviceInfo.getBrand(),
            model: DeviceInfo.getModel(),
            os: DeviceInfo.getSystemName(),
            osVersion: DeviceInfo.getSystemVersion(),
          },
        };

        await processOAuthResponse(newOAuthUser);
      } else if (Platform.OS === 'web') {
        // Web Apple Sign In using Apple's JS SDK
        try {
          if (APPLE_WEB_CLIENT_ID === 'com.smartsaas.signin') {
            handleError('Apple Sign In requires configuration. Please set up your Apple Web Client ID in the environment variables.');
            return;
          }

          await loadAppleSignInScript();
          
          const response = await window.AppleID.auth.signIn();
          
          if (response.authorization && response.authorization.id_token) {
            const newOAuthUser = {
              surname: response.user?.name?.lastName || null,
              name: response.user?.name?.firstName || null,
              authId: response.authorization.code,
              email: response.user?.email || null,
              oauthToken: response.authorization.id_token,
              type: 'apple',
              device: {
                brand: 'Web',
                model: navigator.userAgent,
                os: 'Web',
                osVersion: navigator.appVersion,
              },
            };

            await processOAuthResponse(newOAuthUser);
          } else {
            handleError('Apple Sign In failed: No authorization received');
          }
        } catch (webError) {
          console.error('Web Apple Sign In error:', webError);
          if (webError.error === 'popup_closed_by_user') {
            dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Login was cancelled', type: 'error' } });
          } else {
            handleError('Apple Sign In is temporarily unavailable. Please try another login method.');
          }
        }
      } else {
        handleError('Apple Sign In is not supported on this platform.');
      }
    } catch (e) {
      if (e.code === 'ERR_REQUEST_CANCELED') {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Login was cancelled', type: 'error' } });
      } else {
        console.log(e);
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: e.toString(), type: 'error' } });
      }
    }
  };

  // Process OAuth response
  const processOAuthResponse = async (newOAuthUser) => {
    const res = await apis.handleOAuth(newOAuthUser);
    if (res.status === 202) {
      if (res.data.terms) {
        console.log("user has terms and conditions to accept");
        dispatch({ type: SHOW_TC_MODAL, payload: res.data.userId });

        try {
          const confirm = await getTCConfirm();
          console.log('received a response from the TC Confirmation', confirm);
          dispatch({ type: SHOW_OTP, payload: res.data });
        } catch (e) {
          handleError('Please accept our terms of service to continue');
        }
      } else {
        dispatch({ type: SHOW_OTP, payload: res.data });
      }
    } else {
      await handleTermsAndConditions(res);
      const amendedData = await handleUserData(res);

      dispatch({ type: SUCCESS, data: amendedData });
      // Use replace instead of navigate to avoid navigation typing issues
      (navigation as any).replace('Dashboard');
    }
  };
  // Centralized error handler
  function handleError(message) {
    console.error(message);
    setTimeout(() => dispatch({
      type: SHOW_AUTH_MODAL,
      data: { content: message, type: 'error' }
    }), 1000);
  }

  // Helper function to process user data and handle notification tokens
  async function handleUserData(res) {
    let amendedData = res.data.data;
    if (Platform.OS !== 'web') {
      let activeTokens = amendedData.notification_info?.map(item => item.notification_token) || [];

      if (!activeTokens.length || activeTokens.includes(notificationToken)) {
        await apis.updateNotificationToken(notificationToken, amendedData.userData.id);
      }
      amendedData.expireAt = Date.now() + 1000 * 60 * 60 * 24 * 7;
    }
    return amendedData;
  }

  // Helper function to handle Terms & Conditions
  async function handleTermsAndConditions(res) {
    if (res.data.terms) {
      console.log("Terms and Conditions need acceptance");
      dispatch({ type: HIDE_LOADING_MODAL });
      setTimeout(() => dispatch({ type: SHOW_TC_MODAL, payload: res.data.data.userData.id }), 1000);

      try {
        await getTCConfirm();
      } catch (e) {
        console.error('Error awaiting T&C confirmation', e);
        handleError("Could not fetch terms and conditions, please try again");
      }
    }
  }
  
  // Don't render if loading or not available
  if (isLoading) {
    return null;
  }

  if (!isAppleAvailable && Platform.OS !== 'web') {
    return null;
  }

  // Render platform-specific button
  if (Platform.OS === 'ios' && isAppleAvailable) {
    return (
      <View style={{ maxHeight: 50, minHeight: 50, flex: 1, elevation: 1, backgroundColor: 'white', borderRadius: 30, paddingHorizontal: 20, flexDirection: 'row', justifyContent: 'center', alignContent: 'center', alignItems: 'stretch', overflow: 'hidden' }}>
        {/* Animated gradient background for code indicator */}
        {code && (
          <Animated.View
            style={{
              ...StyleSheet.absoluteFillObject,
              opacity: fadeAnim,
              borderRadius: 30,
            }}
          >
            <LinearGradient
              colors={['#E4D337', '#FF5733']}
              style={{ flex: 1, borderRadius: 30 }}
            />
          </Animated.View>
        )}
        <AppleAuthentication.AppleAuthenticationButton
          buttonType={AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN}
          buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.WHITE}
          cornerRadius={15}
          style={{ flex: 1 }}
          onPress={handleAppleSignIn}
        />
      </View>
    );
  }

  // Web and desktop fallback - custom styled button
  return (
    <View style={{ maxHeight: 50, minHeight: 50, flex: 1, elevation: 1, backgroundColor: 'black', borderRadius: 30, paddingHorizontal: 20, flexDirection: 'row', justifyContent: 'center', alignContent: 'center', alignItems: 'stretch', overflow: 'hidden' }}>
      {/* Animated gradient background for code indicator */}
      {code && (
        <Animated.View
          style={{
            ...StyleSheet.absoluteFillObject,
            opacity: fadeAnim,
            borderRadius: 30,
          }}
        >
          <LinearGradient
            colors={['#E4D337', '#FF5733']}
            style={{ flex: 1, borderRadius: 30 }}
          />
        </Animated.View>
      )}
      <Pressable 
        style={{ 
          flex: 1, 
          flexDirection: 'row', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderRadius: 15,
          paddingHorizontal: 16,
        }}
        onPress={handleAppleSignIn}
      >
        <AntDesign name="apple" size={20} color="white" style={{ marginRight: 8 }} />
        <Text style={{ 
          color: 'white', 
          fontSize: 16, 
          fontWeight: '600',
          textAlign: 'center' 
        }}>
          Continue with Apple
        </Text>
      </Pressable>
    </View>
  );
}
export default AppleButton